import React, { FC, useState, FormEvent, ChangeEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { TextInput } from 'components/TextInput'
import { Button } from 'components/Button'
import { FileInput } from 'components/FileInput'
import { Card } from 'components/Card'
import { Stack } from 'components/Stack'
import { Heading } from 'components/Heading'
import { Text } from 'components/Text'
import { useUpdateOrganization } from 'services/organization'
import { Organization } from 'models/organization'
import { Alert } from 'components/Alert' 

type SettingsProps = {
  organization: Organization
}

export const Settings: FC<SettingsProps> = ({ organization }) => {
  const { t } = useTranslation()
  const [updateOrganization, { status }] = useUpdateOrganization()
  const [logo, setLogo] = useState<File>()
  const [fields, setFields] = useState({
    name: organization.name ?? '',
    email: organization.email ?? '',
    legalRepresentative: organization.legalRepresentative ?? '',
    phoneNumber: organization.phoneNumber ?? '',
  })

  const onFieldChange = (event: ChangeEvent<HTMLInputElement>) => {
    setFields({ ...fields, [event.target.name]: event.target.value })
  }

  const onFormSubmit = (event: FormEvent) => {
    event.preventDefault()

    const data = {
      ...fields,
      // Only add the file if it's a new file
      ...(logo instanceof File ? { logo } : null),
    }

    updateOrganization(data)
  }

  return (
    <Card>
      <Stack space="large">
        <Stack space="xsmall">
          <Heading size="secondary">{t('organization.settings.title')}</Heading>
          <Text>{t('organization.settings.subtitle')}</Text>
        </Stack>

        <form onSubmit={onFormSubmit}>
          <Stack space="medium">
            <TextInput
              required
              name="name"
              label={t('organization.settings.name')}
              value={fields.name}
              onChange={onFieldChange}
            />
            <TextInput
              required
              name="email"
              label={t('organization.settings.email')}
              value={fields.email}
              onChange={onFieldChange}
            />
            <TextInput
              required
              name="legalRepresentative"
              label={t('organization.settings.legal_representative')}
              value={fields.legalRepresentative}
              onChange={onFieldChange}
            />
            <TextInput
              required
              name="phoneNumber"
              label={t('organization.settings.phone_number')}
              value={fields.phoneNumber}
              onChange={onFieldChange}
            />
            <FileInput
              name="logo"
              label={t('organization.settings.logo')}
              description={t('organization.settings.logo_desc')}
              accept=".png,.gif,.jpg,.jpeg"
              value={organization?.logo}
              onChange={setLogo}
            />

            <Button type="submit" loading={status === 'loading'}>
              {t('organization.settings.cta')}
            </Button>
            {status === 'success' && (
              <Alert tone="positive" full>
                {t('common.messages.success')}
              </Alert>
            )}
            {status === 'error' && (
              <Alert tone="critical" full>
                {t('common.messages.error')}
              </Alert>
            )}
          </Stack>
        </form>
      </Stack>
    </Card>
  )
}

Settings.displayName = 'Settings'
